import { createContext, FunctionComponent } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

import type { PlayerConfigOptions, PlayerContext } from '../../../types/global';

import { ResolvedExperiment } from './ExperimentsProvider';

export interface ConfigContextProps extends Config {
  getVariant<T extends string>(name: string): T | undefined;
}

const ConfigContext = createContext<ConfigContextProps>(undefined!);

/**
 * Corner on the player container where the floating player is docked on
 * desktop / tablet-and-above viewports.
 */
export type FloatingPosition =
  'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

/**
 * Mobile-specific layout overrides for the floating player.
 *
 * On mobile (viewport narrower than `breakpoint`) the floating player
 * switches to a sticky-bar layout:
 * - for landscape videos it spans the full viewport width (minus `offset`
 *   on both sides) and docks to the top or bottom edge;
 * - for portrait / vertical videos it takes up roughly half the viewport
 *   width and docks to the left or right half (choose with `verticalAlign`)
 *   so it does not obscure the whole content area.
 */
export interface FloatingMobileConfig {
  /**
   * Edge to dock the sticky bar to.
   * @default 'bottom'
   */
  position?: 'top' | 'bottom';
  /**
   * For portrait / vertical videos only: which half of the viewport the
   * player occupies. Has no effect on landscape videos (they span the full
   * width).
   * @default 'right'
   */
  verticalAlign?: 'left' | 'right';
  /**
   * Viewport width (px) below which the mobile layout kicks in.
   * @default BREAKPOINTS.tablet
   */
  breakpoint?: number;
  /**
   * Maximum width (px) on mobile. The actual width is derived from the
   * video's aspect ratio and viewport, then clamped to this value.
   * If not set, no max-width constraint is applied on mobile.
   */
  maxWidth?: number;
  /**
   * Maximum height (px) on mobile. The actual height is derived from the
   * video's aspect ratio and viewport, then clamped to this value.
   * If not set, no max-height constraint is applied on mobile.
   */
  maxHeight?: number;
  /**
   * Maximum width (px) on mobile for vertical (portrait) content only.
   * If set, overrides `maxWidth` when the video's aspect ratio is < 1.
   */
  verticalMaxWidth?: number;
  /**
   * Maximum height (px) on mobile for vertical (portrait) content only.
   * If set, overrides `maxHeight` when the video's aspect ratio is < 1.
   */
  verticalMaxHeight?: number;
}

/**
 * Configuration for the floating (picture-in-picture-like) player.
 *
 * Pass this as `options.floating` when constructing the player through
 * `getPlayerConfig()` — the wrapper forwards the trigger fields (`mode`,
 * `dismissible`) to JW Player's native `config.floating` and the styling
 * fields to the skin.
 *
 * As an escape hatch for layouts that cannot be expressed through this
 * config (for example docking next to an article column), all layout values
 * are applied as CSS custom properties on the player container and can be
 * overridden from CSS:
 *
 * - `--floating-width`, `--floating-height`
 * - `--floating-inset-block-start`, `--floating-inset-block-end`
 * - `--floating-inset-inline-start`, `--floating-inset-inline-end`
 * - `--floating-border-radius`
 */
export interface FloatingConfig {
  /**
   * When the floating state kicks in. Maps to JW Player's native
   * `config.floating.mode`.
   *
   * - `'notVisible'` — start floating when the player scrolls out of view.
   * - `'always'` — always float.
   * - `'never'` — never float (disables the feature).
   *
   * If this is not set, floating is disabled.
   */
  mode?: 'notVisible' | 'always' | 'never';
  /**
   * Show a dismiss (close) button on the floating player. Maps to
   * JW Player's native `config.floating.dismissible`.
   * @default true
   */
  dismissible?: boolean;
  /**
   * Corner the floating player docks to on desktop.
   * @default 'bottom-right'
   */
  position?: FloatingPosition;
  /**
   * Distance from the viewport edges in px. Used on both desktop and
   * mobile as spacing between the floating player and the edge(s) it is
   * docked to.
   * @default 16
   */
  offset?: number;
  /**
   * Maximum width (px) on desktop. The actual width is derived from the
   * video's aspect ratio and clamped to this value.
   * @default 300
   */
  maxWidth?: number;
  /**
   * Maximum height (px) on desktop. The actual height is derived from the
   * video's aspect ratio and clamped to this value.
   * @default 400
   */
  maxHeight?: number;
  /**
   * Maximum width (px) on desktop for vertical (portrait) content only.
   * If set, overrides `maxWidth` when the video's aspect ratio is < 1.
   */
  verticalMaxWidth?: number;
  /**
   * Maximum height (px) on desktop for vertical (portrait) content only.
   * If set, overrides `maxHeight` when the video's aspect ratio is < 1.
   */
  verticalMaxHeight?: number;
  /**
   * Mobile-specific overrides. See {@link FloatingMobileConfig}.
   */
  mobile?: FloatingMobileConfig;
}

export interface PodcastConfig {
  variant?: 'default' | 'player-only';
}

export interface Config {
  dnaStyle?: PlayerConfigOptions['dnaStyle'];
  context: PlayerContext;
  ui: {
    unmuteButtonEnabled?: boolean;
    videoPreview?: boolean;
    titleOverride?: string;
    hideTitle?: boolean;
  };
  recommended: {
    autoplay: boolean;
  };
  experiments: ResolvedExperiment[];
  playlist?: PlayerConfigOptions['playlist'];
  floating?: FloatingConfig;
  podcast?: PodcastConfig;
}

interface ConfigProviderProps {
  config: Config;
}

export const ConfigProvider: FunctionComponent<ConfigProviderProps> = ({
  config,
  children,
}) => {
  const state = useMemo(() => {
    const { experiments, ui, context, recommended, dnaStyle } = config;

    const getVariant = <T extends string>(name: string) => {
      return experiments.find((e) => e.id === name)?.variant as T | undefined;
    };

    return {
      dnaStyle,
      experiments,
      context,
      recommended,
      ui: {
        ...ui,
        unmuteButtonEnabled: ui.unmuteButtonEnabled ?? context === 'embed',
      },
      getVariant,
      playlist: config.playlist,
      floating: config.floating,
      podcast: config.podcast,
    };
  }, [JSON.stringify(config)]);

  return (
    <ConfigContext.Provider value={state}>{children}</ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const config = useContext(ConfigContext);

  if (process.env.NODE_ENV !== 'production' && !config) {
    console.warn(
      'Config context is undefined, please verify you are calling useConfig() as child of a <ConfigProvider> component.'
    );
  }

  return config;
};
